import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown } from "components";
import { useAnchor } from "hooks";
import { ComponentIds } from "types/enums/componentIds";
import { Features11Content } from "types/graphql/generated";

interface Props {
  content: Features11Content;
}

const Features11: VFC<Props> = withErrorBoundary(
  ({ content: { features11Description, features11Title, features11Disclaimer, features11FeatureList } }) => {
    useAnchor("features11");
    useAnchor(ComponentIds.EmployeeFees);

    return (
      <StyledFeatures11 className={`features11 Content ${ComponentIds.EmployeeFees}`}>
        <div className="Features__container">
          <div className="Features__featuresContainer">
            <ul className="Features__features">
              {features11FeatureList.map(({ feature, id }) => (
                <li key={id} className="Features__feature Color_dusk80 Font_24_32_600 lg:Font_18_26_700">
                  {feature}
                </li>
              ))}
            </ul>
            {features11Disclaimer && (
              <Markdown className="Features__disclaimerMobile Color_tertiary Font_12_16_450 lg:Font_12_16_450">
                {features11Disclaimer}
              </Markdown>
            )}
          </div>

          <div className="Features__mainContent">
            <h2 className="Features__title Font_48_54_600 lg:Font_40_48_600">{features11Title}</h2>
            <Markdown className="Features__description Color_secondary Font_24_32_450 xl:Font_18_26_450">
              {features11Description}
            </Markdown>
            {features11Disclaimer && (
              <Markdown className="Features__disclaimerDesktop Color_tertiary Font_12_16_450">
                {features11Disclaimer}
              </Markdown>
            )}
          </div>
        </div>
      </StyledFeatures11>
    );
  },
  {}
);

const StyledFeatures11 = styled.section`
  margin-top: -64px;
  padding-top: 64px;

  .Features__container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${({ theme }) => theme.colors.coral["05"]};
    padding: 80px 56px;
  }

  .Features__mainContent {
    max-width: 720px;
    margin-left: 80px;
  }

  .Features__featuresContainer {
    display: flex;
    flex-direction: column;
    max-width: 400px;
    flex-shrink: 0;
  }

  .Features__features {
    display: flex;
    flex-direction: column;
  }

  .Features__feature {
    width: fit-content;
    padding: 8px 24px;
    border-radius: 64px;
  }

  .Features__feature:not(:first-child) {
    margin-top: 24px;
  }

  .Features__feature:nth-child(4n + 1) {
    background-color: ${({ theme }) => theme.colors.eucalyptus["30"]};
    color: ${({ theme }) => theme.colors.eucalyptus["80"]};
  }

  .Features__feature:nth-child(4n + 2) {
    background-color: ${({ theme }) => theme.colors.coral["40"]};
    color: ${({ theme }) => theme.colors.coral["80"]};
  }

  .Features__feature:nth-child(4n + 3) {
    background-color: ${({ theme }) => theme.colors.dusk["20"]};
    color: ${({ theme }) => theme.colors.dusk["80"]};
  }

  .Features__feature:nth-child(4n + 4) {
    background-color: #ffcd85;
    color: ${({ theme }) => theme.colors.coral["80"]};
  }

  .Features__description {
    margin-top: 24px;

    p:not(:first-child) {
      margin-top: 20px;
    }
  }

  .Features__disclaimerDesktop {
    margin-top: 40px;
  }

  .Features__disclaimerMobile {
    display: none;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Features__container {
      padding: 64px 56px;
    }

    .Features__mainContent {
      margin-left: 64px;
    }

    .Features__title {
      max-width: 400px;
    }

    .Features__description {
      p:not(:first-child) {
        margin-top: 16px;
      }
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    &.Content {
      padding-left: 0;
      padding-right: 0;
    }

    .Features__container {
      flex-direction: column-reverse;
      align-items: flex-start;
      padding: 80px 32px;
    }

    .Features__mainContent {
      margin-left: 0;
    }

    .Features__title {
      max-width: 100%;
    }

    .Features__disclaimerDesktop {
      display: none;
    }

    .Features__disclaimerMobile {
      display: block;
      margin-top: 40px;
    }

    .Features__featuresContainer {
      max-width: 100%;
      margin-top: 40px;
    }

    .Features__features {
      max-width: calc(552px + 16px);
      flex-flow: row wrap;
      margin-left: -16px;
      margin-top: -16px;
    }

    .Features__features .Features__feature {
      margin-left: 16px;
      margin-top: 16px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Features__container {
      padding: 64px 24px;
    }

    .Features__featuresContainer {
      margin-top: 32px;
    }

    .Features__features {
      flex-direction: column;
    }

    .Features__disclaimerMobile {
      margin-top: 32px;
    }
  }
`;

gql`
  fragment Features11Content on ComponentFeaturesFeatures11 {
    features11Title
    features11Description
    features11Disclaimer
    features11FeatureList {
      feature
      id
    }
  }
`;

export { Features11 };
export type { Props as Features11Props };
